// @flow

import React, { Component, createRef } from 'react';
import launchDashboardHtml from './LaunchDashboardData';

const LAUNCH_BASELINE_DATE = Date.UTC(2026, 0, 1);
const LAUNCH_CHECKPOINT_DATE = Date.UTC(2026, 4, 31);
const OVERALL_READINESS = 61;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const formatUtcDate = (date: Date): string => {
    const year = date.getUTCFullYear();
    const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${date.getUTCDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getLaunchMetrics = () => {
    const now = new Date();
    const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const totalRunwayMs = LAUNCH_CHECKPOINT_DATE - LAUNCH_BASELINE_DATE;
    const elapsedRunwayMs = clamp(nowUtc - LAUNCH_BASELINE_DATE, 0, totalRunwayMs);
    const timeConsumed = Math.round((elapsedRunwayMs / totalRunwayMs) * 100);
    const daysToCheckpoint = Math.max(0, Math.ceil((LAUNCH_CHECKPOINT_DATE - nowUtc) / (24 * 60 * 60 * 1000)));

    return {
        reportDate: formatUtcDate(now),
        timeConsumed,
        daysToCheckpoint,
        readinessGapLabel: `${OVERALL_READINESS - timeConsumed >= 0 ? '+' : ''}${OVERALL_READINESS - timeConsumed} pts`,
    };
};

const buildLaunchDashboardHtml = () => {
    const { reportDate, timeConsumed, daysToCheckpoint, readinessGapLabel } = getLaunchMetrics();

    return launchDashboardHtml
        .replace(
            'html { scroll-behavior: smooth; }',
            [
                'html {',
                '        scroll-behavior: smooth;',
                '        min-height: 100%;',
                '        overflow-x: hidden;',
                '        background:',
                '          radial-gradient(circle at top left, rgba(209, 122, 45, 0.18), transparent 26%),',
                '          radial-gradient(circle at right 10% top 8%, rgba(17, 121, 92, 0.12), transparent 18%),',
                '          linear-gradient(180deg, var(--page-top) 0%, var(--page-mid) 56%, var(--page-bottom) 100%);',
                '        background-color: var(--page-mid);',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      body {',
                '        margin: 0;',
                '        min-height: 100vh;',
            ].join('\n'),
            [
                '      body {',
                '        margin: 0;',
                '        min-height: 100vh;',
                '        width: 100%;',
                '        max-width: 100%;',
                '        background-color: var(--page-mid);',
                '        overflow-x: hidden;',
            ].join('\n'),
        )
        .replace(
            '      button { font: inherit; }',
            [
                '      button { font: inherit; }',
                '      .top-shell > *,',
                '      .dashboard-grid > *,',
                '      .hero-head > *,',
                '      .timeline-top > *,',
                '      .section-header > *,',
                '      .component-top > *,',
                '      .dialog-header > * {',
                '        min-width: 0;',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .page {',
                '        width: min(calc(100% - 28px), var(--max));',
                '        margin: 0 auto;',
                '        padding: 18px 0 44px;',
                '        position: relative;',
                '      }',
            ].join('\n'),
            [
                '      .page {',
                '        width: min(calc(100% - 28px), var(--max));',
                '        margin: 0 auto;',
                '        padding: 18px 0 44px;',
                '        position: relative;',
                '        max-width: 100%;',
                '        isolation: isolate;',
              '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .hero {',
                '        position: relative;',
                '        overflow: hidden;',
                '        padding: 20px 22px;',
                '        border-radius: var(--radius-xl);',
                '        background: linear-gradient(135deg, rgba(24, 21, 19, 0.97), rgba(53, 41, 35, 0.92));',
                '        color: #fff8ef;',
                '        box-shadow: var(--shadow);',
                '      }',
            ].join('\n'),
            [
                '      .hero {',
                '        position: relative;',
                '        overflow: hidden;',
                '        padding: 20px 22px;',
                '        border-radius: var(--radius-xl);',
                '        background: linear-gradient(135deg, rgba(255, 251, 245, 0.98), rgba(247, 239, 229, 0.96));',
                '        color: var(--ink);',
                '        border: 1px solid rgba(120, 95, 72, 0.12);',
                '        box-shadow: 0 18px 44px rgba(67, 50, 37, 0.1);',
                '      }',
            ].join('\n'),
        )
        .replace(
            '      .hero::after {\n        content: "";\n        position: absolute;\n        right: -84px;\n        top: -88px;\n        width: 250px;\n        height: 250px;\n        border-radius: 50%;\n        background: radial-gradient(circle, rgba(209, 122, 45, 0.42), transparent 64%);\n      }',
            [
                '      .hero::after {',
                '        content: "";',
                '        position: absolute;',
                '        right: -84px;',
                '        top: -88px;',
                '        width: 250px;',
                '        height: 250px;',
                '        border-radius: 50%;',
                '        background: radial-gradient(circle, rgba(209, 122, 45, 0.24), transparent 68%);',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .brand-mark {',
                '        width: 54px;',
                '        color: #fff8ef;',
                '        line-height: 0;',
                '      }',
            ].join('\n'),
            [
                '      .brand-mark {',
                '        width: 54px;',
                '        color: var(--ink);',
                '        line-height: 0;',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .stamp {',
                '        padding: 9px 13px;',
                '        border-radius: 999px;',
                '        background: rgba(255, 248, 239, 0.08);',
                '        border: 1px solid rgba(255, 248, 239, 0.14);',
                '        font-size: 12px;',
                '        color: rgba(255, 248, 239, 0.92);',
                '        white-space: nowrap;',
                '      }',
            ].join('\n'),
            [
                '      .stamp {',
                '        padding: 9px 13px;',
                '        border-radius: 999px;',
                '        background: rgba(24, 21, 19, 0.04);',
                '        border: 1px solid rgba(24, 21, 19, 0.08);',
                '        font-size: 12px;',
                '        color: rgba(24, 21, 19, 0.82);',
                '        white-space: nowrap;',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .theme-toggle {',
                '        padding: 9px 13px;',
                '        border-radius: 999px;',
                '        border: 1px solid rgba(255, 248, 239, 0.14);',
                '        background: rgba(255, 248, 239, 0.08);',
                '        color: rgba(255, 248, 239, 0.92);',
                '        cursor: pointer;',
                '      }',
                '      .theme-toggle:hover { background: rgba(255, 248, 239, 0.14); }',
            ].join('\n'),
            [
                '      .theme-toggle {',
                '        padding: 9px 13px;',
                '        border-radius: 999px;',
                '        border: 1px solid rgba(24, 21, 19, 0.1);',
                '        background: rgba(24, 21, 19, 0.04);',
                '        color: rgba(24, 21, 19, 0.86);',
                '        cursor: pointer;',
                '      }',
                '      .theme-toggle:hover { background: rgba(24, 21, 19, 0.08); }',
            ].join('\n'),
        )
        .replace(
            [
                '      .hero-summary {',
                '        margin-top: 12px;',
                '        max-width: 74ch;',
                '        font-size: 14px;',
                '        line-height: 1.58;',
                '        color: rgba(255, 248, 239, 0.86);',
                '      }',
            ].join('\n'),
            [
                '      .hero-summary {',
                '        margin-top: 12px;',
                '        max-width: 74ch;',
                '        font-size: 14px;',
                '        line-height: 1.58;',
                '        color: var(--muted);',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .meta-pill {',
                '        padding: 7px 11px;',
                '        border-radius: 999px;',
                '        background: rgba(255, 248, 239, 0.08);',
                '        border: 1px solid rgba(255, 248, 239, 0.14);',
                '        font-size: 12px;',
                '      }',
            ].join('\n'),
            [
                '      .meta-pill {',
                '        padding: 7px 11px;',
                '        border-radius: 999px;',
                '        background: rgba(24, 21, 19, 0.04);',
                '        border: 1px solid rgba(24, 21, 19, 0.08);',
                '        font-size: 12px;',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .focus-chip {',
                '        padding: 12px 14px;',
                '        border-radius: 16px;',
                '        background: rgba(255, 248, 239, 0.08);',
                '        border: 1px solid rgba(255, 248, 239, 0.14);',
                '      }',
            ].join('\n'),
            [
                '      .focus-chip {',
                '        padding: 12px 14px;',
                '        border-radius: 16px;',
                '        background: rgba(24, 21, 19, 0.04);',
                '        border: 1px solid rgba(24, 21, 19, 0.08);',
                '      }',
            ].join('\n'),
        )
        .replace(
            [
                '      .focus-chip strong {',
                '        display: block;',
                '        margin-bottom: 6px;',
                '        font-size: 11px;',
                '        text-transform: uppercase;',
                '        letter-spacing: 0.14em;',
                '        color: rgba(255, 248, 239, 0.72);',
                '      }',
            ].join('\n'),
            [
                '      .focus-chip strong {',
                '        display: block;',
                '        margin-bottom: 6px;',
                '        font-size: 11px;',
                '        text-transform: uppercase;',
                '        letter-spacing: 0.14em;',
                '        color: var(--muted);',
                '      }',
            ].join('\n'),
        )
        .replace(
            '      body[data-theme="dark"] {\n        --bg: #151311;\n        --panel: rgba(24, 21, 19, 0.88);\n        --panel-strong: #211b16;\n        --panel-soft: rgba(30, 25, 21, 0.96);\n        --ink: #f4eee6;\n        --muted: #c0b6aa;\n        --line: rgba(244, 238, 230, 0.1);\n        --surface-border: rgba(244, 238, 230, 0.08);\n        --page-top: #181412;\n        --page-mid: #14110f;\n        --page-bottom: #0e0c0b;\n        --grid-line: rgba(244, 238, 230, 0.05);\n        --overview-bg: linear-gradient(180deg, rgba(31, 26, 22, 0.98), rgba(24, 20, 17, 0.95));\n        --section-bg: rgba(24, 21, 19, 0.9);\n        --bottom-bg: linear-gradient(180deg, rgba(0,0,0,0), rgba(19,17,15,0.88));\n        --dialog-bg: linear-gradient(180deg, rgba(29, 24, 20, 0.99), rgba(20, 17, 14, 0.97));\n        --dialog-card-bg: rgba(37, 31, 27, 0.84);\n        --button-surface: rgba(35, 30, 26, 0.95);\n        --safe-soft: rgba(17, 121, 92, 0.22);\n        --watch-soft: rgba(209, 122, 45, 0.24);\n        --risk-soft: rgba(180, 66, 51, 0.24);\n        --shadow: 0 20px 50px rgba(0, 0, 0, 0.32);\n      }',
            [
                '      body[data-theme="dark"] {',
                '        --bg: #151311;',
                '        --panel: rgba(24, 21, 19, 0.88);',
                '        --panel-strong: #211b16;',
                '        --panel-soft: rgba(30, 25, 21, 0.96);',
                '        --ink: #f4eee6;',
                '        --muted: #c0b6aa;',
                '        --line: rgba(244, 238, 230, 0.1);',
                '        --surface-border: rgba(244, 238, 230, 0.08);',
                '        --page-top: #181412;',
                '        --page-mid: #14110f;',
                '        --page-bottom: #0e0c0b;',
                '        --grid-line: rgba(244, 238, 230, 0.05);',
                '        --overview-bg: linear-gradient(180deg, rgba(31, 26, 22, 0.98), rgba(24, 20, 17, 0.95));',
                '        --section-bg: rgba(24, 21, 19, 0.9);',
                '        --bottom-bg: linear-gradient(180deg, rgba(0,0,0,0), rgba(19,17,15,0.88));',
                '        --dialog-bg: linear-gradient(180deg, rgba(29, 24, 20, 0.99), rgba(20, 17, 14, 0.97));',
                '        --dialog-card-bg: rgba(37, 31, 27, 0.84);',
                '        --button-surface: rgba(35, 30, 26, 0.95);',
                '        --safe-soft: rgba(17, 121, 92, 0.22);',
                '        --watch-soft: rgba(209, 122, 45, 0.24);',
                '        --risk-soft: rgba(180, 66, 51, 0.24);',
                '        --shadow: 0 20px 50px rgba(0, 0, 0, 0.32);',
                '      }',
                '',
                '      body[data-theme="dark"] .hero {',
                '        background: linear-gradient(135deg, rgba(24, 21, 19, 0.97), rgba(53, 41, 35, 0.92));',
                '        color: #fff8ef;',
                '        border-color: rgba(255, 248, 239, 0.08);',
                '        box-shadow: var(--shadow);',
                '      }',
                '',
                '      body[data-theme="dark"] .brand-mark { color: #fff8ef; }',
                '      body[data-theme="dark"] .stamp,',
                '      body[data-theme="dark"] .theme-toggle,',
                '      body[data-theme="dark"] .meta-pill,',
                '      body[data-theme="dark"] .focus-chip {',
                '        background: rgba(255, 248, 239, 0.08);',
                '        border-color: rgba(255, 248, 239, 0.14);',
                '      }',
                '',
                '      body[data-theme="dark"] .stamp,',
                '      body[data-theme="dark"] .theme-toggle {',
                '        color: rgba(255, 248, 239, 0.92);',
                '      }',
                '',
                '      body[data-theme="dark"] .theme-toggle:hover { background: rgba(255, 248, 239, 0.14); }',
                '      body[data-theme="dark"] .hero-summary { color: rgba(255, 248, 239, 0.86); }',
                '      body[data-theme="dark"] .focus-chip strong { color: rgba(255, 248, 239, 0.72); }',
            ].join('\n'),
        )
        .replace(
            [
                '      .dialog-header { display: flex; justify-content: space-between; gap: 16px; align-items: start; }',
                '      .dialog-header h3 { margin-top: 6px; font-size: 28px; letter-spacing: -0.04em; }',
                '      .dialog-close { padding: 10px 14px; border-radius: 999px; border: 1px solid var(--line); background: var(--button-surface); color: var(--ink); cursor: pointer; }',
                '      .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin-top: 16px; }',
                '      .dialog-card { padding: 16px; border-radius: 18px; background: var(--dialog-card-bg); border: 1px solid var(--line); }',
                '      .dialog-card-wide { grid-column: 1 / -1; }',
                '      .dialog-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--muted); }',
                '      .dialog-card p { margin-top: 8px; font-size: 14px; line-height: 1.58; color: var(--muted); }',
                '      .dialog-card ul { margin: 10px 0 0; padding-left: 18px; display: grid; gap: 8px; color: var(--muted); }',
            ].join('\n'),
            [
                '      .dialog-header { display: flex; justify-content: space-between; gap: 16px; align-items: start; color: var(--ink); }',
                '      .dialog-header h3 { margin-top: 6px; font-size: 28px; letter-spacing: -0.04em; color: var(--ink); }',
                '      .dialog-close { padding: 10px 14px; border-radius: 999px; border: 1px solid var(--line); background: var(--button-surface); color: var(--ink); cursor: pointer; }',
                '      .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin-top: 16px; }',
                '      .dialog-card { padding: 16px; border-radius: 18px; background: var(--dialog-card-bg); border: 1px solid var(--line); }',
                '      .dialog-card-wide { grid-column: 1 / -1; }',
                '      .dialog-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--muted); }',
                '      .dialog-card h3 { color: var(--ink); }',
                '      .dialog-card p { margin-top: 8px; font-size: 14px; line-height: 1.58; color: var(--muted); }',
                '      .dialog-card ul { margin: 10px 0 0; padding-left: 18px; display: grid; gap: 8px; color: var(--muted); }',
            ].join('\n'),
        )
        .replace(
            [
                '      @media (max-width: 640px) {',
                '        .page { width: min(calc(100% - 18px), var(--max)); padding-top: 10px; }',
                '        .hero, .overview-card, .section-header, .timeline-wrap, .side-stack, .bottom-line { padding-left: 16px; padding-right: 16px; }',
                '        .hero-head, .timeline-top, .runway-grid { grid-template-columns: 1fr; display: grid; }',
                '        .timeline-item { grid-template-columns: 42px 1fr; }',
                '      }',
            ].join('\n'),
            [
                '      @media (max-width: 640px) {',
                '        .page { width: 100%; max-width: 100%; padding: 0 0 24px; }',
                '        .top-shell, .dashboard-grid, .signal-grid, .focus-strip, .checklist-grid, .dialog-grid, .runway-labels { grid-template-columns: 1fr; }',
                '        .hero, .overview-card, .section-header, .timeline-wrap, .side-stack, .bottom-line, .checklist-grid { padding-left: 14px; padding-right: 14px; }',
                '        .section-header, .dialog-header { display: grid; }',
                '        .hero-head, .timeline-top, .runway-grid { grid-template-columns: 1fr; display: grid; }',
                '        .hero-actions { justify-content: start; }',
                '        .stamp { white-space: normal; }',
                '        .hero-summary, .section-note, .timeline-card h3, .timeline-goal, .timeline-status, .signal-value, .component-card h3, .component-note, .bottom-line p, .dialog-card p, .dialog-card li { overflow-wrap: anywhere; word-break: break-word; }',
                '        .section-card, .overview-card, .timeline-card, .side-card, .component-card, .signal, .phase-dialog-shell, .modal-dialog { width: 100%; max-width: 100%; }',
                '        .hero::after { display: none; }',
                '        .timeline-item { grid-template-columns: 42px 1fr; }',
                '        .directive-card, .risk-card { grid-template-columns: 40px 1fr; gap: 12px; }',
                '        .component-note { min-height: 0; }',
                '        .modal-dialog { max-width: calc(100vw - 16px); }',
                '        .phase-dialog-shell { padding: 16px; border-radius: 20px; }',
                '      }',
            ].join('\n'),
        )
        .replace('As of 2026-03-14', `As of ${reportDate}`)
        .replace('style="width: 48%"', `style="width: ${timeConsumed}%"`)
        .replace('style="left: calc(48% - 1px)"', `style="left: calc(${timeConsumed}% - 1px)"`)
        .replace('Time consumed: <strong>48%</strong>', `Time consumed: <strong>${timeConsumed}%</strong>`)
        .replace('Days to checkpoint: <strong>78</strong>', `Days to checkpoint: <strong>${daysToCheckpoint}</strong>`)
        .replace('<span class="signal-value">+13 pts</span>', `<span class="signal-value">${readinessGapLabel}</span>`);
};

type LaunchStatusDashboardState = {
    compactViewport: boolean,
    frameHeight: number | null,
};

export default class LaunchStatusDashboard extends Component<any, LaunchStatusDashboardState> {
    iframeRef: { current: null | HTMLIFrameElement };

    resizeObserver: ResizeObserver | null;

    constructor(props: any) {
        super(props);

        this.iframeRef = createRef();
        this.resizeObserver = null;
        this.state = {
            compactViewport: false,
            frameHeight: null,
        };
    }

    componentDidMount() {
        this.syncViewportMode();
        window.addEventListener('resize', this.handleWindowResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize);
        this.disconnectResizeObserver();
    }

    handleWindowResize = () => {
        this.syncViewportMode();
        this.updateFrameHeight();
    };

    handleIframeLoad = () => {
        this.syncViewportMode();
        this.attachResizeObserver();
        this.updateFrameHeight();
    };

    syncViewportMode() {
        const compactViewport = typeof window !== 'undefined' && window.innerWidth <= 640;
        if (compactViewport !== this.state.compactViewport) {
            this.setState({ compactViewport }, () => this.updateFrameHeight());
        }
    }

    getIframeDocument(): null | Document {
        const iframe = this.iframeRef.current;
        if (!iframe) {
            return null;
        }

        return iframe.contentDocument;
    }

    disconnectResizeObserver() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    attachResizeObserver() {
        this.disconnectResizeObserver();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const iframeDocument = this.getIframeDocument();
        if (!iframeDocument) {
            return;
        }

        const target = iframeDocument.body || iframeDocument.documentElement;
        if (!target) {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => this.updateFrameHeight());
        this.resizeObserver.observe(target);
    }

    updateFrameHeight() {
        if (!this.state.compactViewport) {
            if (this.state.frameHeight !== null) {
                this.setState({ frameHeight: null });
            }
            return;
        }

        const iframeDocument = this.getIframeDocument();
        if (!iframeDocument) {
            return;
        }

        const nextHeight = Math.max(
            iframeDocument.documentElement ? iframeDocument.documentElement.scrollHeight : 0,
            iframeDocument.body ? iframeDocument.body.scrollHeight : 0,
        );

        if (nextHeight > 0 && nextHeight !== this.state.frameHeight) {
            this.setState({ frameHeight: nextHeight });
        }
    }

    render() {
        const { compactViewport, frameHeight } = this.state;
        const frameClassName = `launch-status-dashboard-frame${compactViewport ? ' is-mobile' : ''}`;
        const shellClassName = `launch-status-dashboard-shell${compactViewport ? ' is-mobile' : ''}`;

        return (
            <div className={shellClassName}>
                <div className={frameClassName}>
                    <iframe
                        ref={this.iframeRef}
                        title="Launch Status Dashboard"
                        srcDoc={buildLaunchDashboardHtml()}
                        style={{
                            width: '100%',
                            height: compactViewport && frameHeight ? `${frameHeight}px` : '100%',
                            border: 'none',
                            display: 'block',
                            background: 'transparent',
                        }}
                        onLoad={this.handleIframeLoad}
                        scrolling={compactViewport ? 'no' : 'yes'}
                    />
                </div>
            </div>
        );
    }
}
